import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, ListGroup, Collapse } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAnalyticsReportListAction,
  getAnalyticsAnalyticsReportAction
} from '../../../redux/features/analyticsReports/analyticsReportsSlice';
import { getSessionStorage } from '../../../utils/helpers';

export default function NavList({ mobileMenuOpen, setMobileMenuOpen }) {
  const permissionLevel = getSessionStorage('PERMISSION_LEVEL');
  const isAssessor = getSessionStorage('IS_ASSESSOR');
  const showAssessmentCycleSAACA = ['SA', 'A', 'CA'].includes(permissionLevel);
  const showAssessmentCycleUHR = (permissionLevel === 'U' && isAssessor === 'true');
  const showAssessmentCycle = showAssessmentCycleSAACA || showAssessmentCycleUHR;

  const [dropdownVisibility, setDropdownVisibility] = useState({
    assestCycle: false,
    techAssest: false,
    employeeTask: false,
    Admin: false,
    reports: false,
    assessor: false,
    Analytics: false,
    userEmployeeTask: false,
    userReports: false,
    userAnalytics: false,
    more: false
  });

  // Mobile menu collapse states
  const [mobileCollapseStates, setMobileCollapseStates] = useState({
    assessmentCycle: false,
    technicalAssessment: false,
    assessor: false,
    employeeTasks: false,
    admin: false,
    reports: false,
    analytics: false
  });

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const reportList = useSelector((state) => state?.analyticsReports?.analytics);
  const [id, setId] = useState();
  const dispatch = useDispatch();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Define all navigation items with their visibility conditions
  const allNavItems = [
    {
      id: 'assessmentCycle',
      label: 'Assessment Cycle',
      show: ['SA', 'A'].includes(permissionLevel),
      component: 'assessmentCycle'
    },
    {
      id: 'technicalAssessment',
      label: 'Technical Assessment',
      show: ['SA', 'A', 'CA'].includes(permissionLevel),
      component: 'technicalAssessment'
    },
    {
      id: 'assessor',
      label: 'Assessor',
      show: isAssessor === 'true',
      component: 'assessor'
    },
    {
      id: 'employeeTasks',
      label: 'Employees Tasks',
      show: true,
      component: 'employeeTasks'
    },
    {
      id: 'admin',
      label: 'Admin',
      show: ['SA', 'A', 'HR'].includes(permissionLevel),
      component: 'admin'
    },
    {
      id: 'reports',
      label: 'Reports',
      show: true,
      component: 'reports'
    },
    {
      id: 'analytics',
      label: 'Analytics',
      show: true,
      component: 'analytics'
    }
  ].filter(item => item.show);

  useEffect(() => {
    if (id) {
      dispatch(getAnalyticsAnalyticsReportAction(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(getAnalyticsReportListAction());
  }, [dispatch]);

  const toggleDropdown = (dropdownName) => {
    setDropdownVisibility((prevVisibility) => {
      const updatedVisibility = {};
      Object.keys(prevVisibility).forEach((key) => {
        updatedVisibility[key] = key === dropdownName;
      });
      return updatedVisibility;
    });

    if (dropdownRefs[dropdownName]?.current) {
      const classArray = dropdownRefs[dropdownName].current.childNodes;
      for (let i = 0; i < classArray.length; i++) {
        if (classArray[i].children && classArray[i].children.length > 0) {
          classArray[i].classList.add('show');
        }
      }
    }
  };

  const toggleMobileCollapse = (itemId) => {
  setMobileCollapseStates(prevState => {
    // Create a new state object with all items set to false
    const newState = {};
    Object.keys(prevState).forEach(key => {
      newState[key] = false;
    });
    
    // Only set the clicked item to the opposite of its current state
    newState[itemId] = !prevState[itemId];
    
    return newState;
  });
};

  // Track if mouse is over dropdown or submenu, and a close timeout
  const [hoveredDropdown, setHoveredDropdown] = useState(null);
  const closeTimeout = useRef();

  const closeDropdown = () => {
    setDropdownVisibility({
      assestCycle: false,
      techAssest: false,
      employeeTask: false,
      Admin: false,
      reports: false,
      assessor: false,
      Analytics: false,
      userEmployeeTask: false,
      userReports: false,
      userAnalytics: false,
      more: false
    });
    setHoveredDropdown(null);
  };

  // Helper to start/clear close timeout
  const startCloseTimeout = () => {
    closeTimeout.current = setTimeout(() => {
      closeDropdown();
    }, 120);
  };
  const clearCloseTimeout = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  };

  const handleLinkClick = () => {
    closeDropdown();
    if (setMobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  const dropdownRefs = {
    assestCycle: useRef(null),
    techAssest: useRef(null),
    employeeTask: useRef(null),
    Admin: useRef(null),
    reports: useRef(null),
    assessor: useRef(null),
    Analytics: useRef(null),
    userEmployeeTask: useRef(null),
    userReports: useRef(null),
    userAnalytics: useRef(null),
    more: useRef(null)
  };

  function handleonMouseLeave(event, dropdownName) {
    // Only handle mouse leave on larger screens
    if (windowWidth >= 992) {
      for (const key in dropdownRefs) {
        if (dropdownRefs[key].current && !dropdownRefs[key].current.contains(event.target)) {
          setDropdownVisibility((prevVisibility) => ({
            ...prevVisibility,
            [key]: false
          }));
        }
      }
      const classArray = dropdownRefs[dropdownName]?.current?.childNodes;
      for (let i = 0; i < classArray?.length; i++) {
        if (classArray[i].children?.length > 0) {
          classArray[i].classList.remove('show');
        }
      }
    }
  }

  useEffect(() => {
    function handleClickOutside(event) {
      for (const key in dropdownRefs) {
        if (dropdownRefs[key].current && !dropdownRefs[key].current.contains(event.target)) {
          setDropdownVisibility((prevVisibility) => ({
            ...prevVisibility,
            [key]: false
          }));
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Render mobile menu items with collapsible submenus
  const renderMobileNavItem = (itemId) => {
    const baseClass = "nav-link d-block py-2 px-3";
    const isExpanded = mobileCollapseStates[itemId];

    switch (itemId) {
      case 'assessmentCycle':
        return (
          <div key="assessmentCycle" className="mobile-nav-section">
            <div 
              className="mobile-nav-header d-flex justify-content-between align-items-center"
              onClick={() => toggleMobileCollapse('assessmentCycle')}
            >
              <span>Assessment Cycle</span>
              <i className={`feather ${isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'}`}></i>
            </div>
            <Collapse in={isExpanded}>
              <div>
                <Link to="/AssessmentCycle/AssessmentCycle" className={baseClass} onClick={handleLinkClick}>
                  Assessment Cycle
                </Link>
                <Link to="/AssessmentCycle/QuestionBank" className={baseClass} onClick={handleLinkClick}>
                  Question Bank
                </Link>
                <Link to="/AssessmentCycle/AssessorEmployeeMapping" className={baseClass} onClick={handleLinkClick}>
                  Assessor Employee Mapping
                </Link>
              </div>
            </Collapse>
          </div>
        );

      case 'technicalAssessment':
        return (
          <div key="technicalAssessment" className="mobile-nav-section">
            <div 
              className="mobile-nav-header d-flex justify-content-between align-items-center"
              onClick={() => toggleMobileCollapse('technicalAssessment')}
            >
              <span>Technical Assessment</span>
              <i className={`feather ${isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'}`}></i>
            </div>
            <Collapse in={isExpanded}>
              <div>
                <Link to="/TechnicalAssessment/TechnicalAssessments" className={baseClass} onClick={handleLinkClick}>
                  Technical Assessment
                </Link>
                <Link to="/TechnicalAssessment/TechnicalQuestionBank" className={baseClass} onClick={handleLinkClick}>
                  Technical Question Bank
                </Link>
                {['SA', 'A'].includes(permissionLevel) && (
                  <>
                    <Link to="/TechnicalAssessment/ManageSmeMappings" className={baseClass} onClick={handleLinkClick}>
                      Manage SME Mappings
                    </Link>
                    <Link to="/TechnicalAssessment/ApproveReviewModifications" className={baseClass} onClick={handleLinkClick}>
                      Approve Review Modifications
                    </Link>
                  </>
                )}
              </div>
            </Collapse>
          </div>
        );

      case 'assessor':
        return (
          <div key="assessor" className="mobile-nav-section">
            <div 
              className="mobile-nav-header d-flex justify-content-between align-items-center"
              onClick={() => toggleMobileCollapse('assessor')}
            >
              <span>Assessor</span>
              <i className={`feather ${isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'}`}></i>
            </div>
            <Collapse in={isExpanded}>
              <div>
                <Link to="/Assessor/UserAnalysis/UsersList" className={baseClass} onClick={handleLinkClick}>
                  Evaluate Employee
                </Link>
              </div>
            </Collapse>
          </div>
        );

      case 'employeeTasks':
        return (
          <div key="employeeTasks" className="mobile-nav-section">
            <div 
              className="mobile-nav-header d-flex justify-content-between align-items-center"
              onClick={() => toggleMobileCollapse('employeeTasks')}
            >
              <span>Employees Tasks</span>
              <i className={`feather ${isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'}`}></i>
            </div>
            <Collapse in={isExpanded}>
              <div>
                <Link to="/EmployeesTask/AssessmentCycle" className={baseClass} onClick={handleLinkClick}>
                  My Evaluation Form
                </Link>
                <Link to="/EmployeesTask/SelfAssessments" className={baseClass} onClick={handleLinkClick}>
                  Self Assessment
                </Link>
                <Link to="/EmployeesTask/QuestionBankReview" className={baseClass} onClick={handleLinkClick}>
                  Question Bank Review
                </Link>
              </div>
            </Collapse>
          </div>
        );

      case 'admin':
        return (
          <div key="admin" className="mobile-nav-section">
            <div 
              className="mobile-nav-header d-flex justify-content-between align-items-center"
              onClick={() => toggleMobileCollapse('admin')}
            >
              <span>Admin</span>
              <i className={`feather ${isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'}`}></i>
            </div>
            <Collapse in={isExpanded}>
              <div>
                {['SA', 'A'].includes(permissionLevel) && (
                  <>
                    <Link to="/Admin/ManageEmployee" className={baseClass} onClick={handleLinkClick}>
                      Manage Employee
                    </Link>
                    <Link to="/Admin/ManageAssessor" className={baseClass} onClick={handleLinkClick}>
                      Manage Assessor
                    </Link>
                    <Link to="/Admin/ManageSkill" className={baseClass} onClick={handleLinkClick}>
                      Manage Skill
                    </Link>
                  </>
                )}
                <Link to="/Admin/ManageExternalUser" className={baseClass} onClick={handleLinkClick}>
                  Manage External User
                </Link>
                {['SA', 'A'].includes(permissionLevel) && (
                  <>
                    <Link to="/Admin/ManageEmail" className={baseClass} onClick={handleLinkClick}>
                      Manage Email
                    </Link>
                    <Link to="/Admin/ManageEmployeeCycle" className={baseClass} onClick={handleLinkClick}>
                      Manage Employee Cycle
                    </Link>
                  </>
                )}
              </div>
            </Collapse>
          </div>
        );

      case 'reports':
        return (
          <div key="reports" className="mobile-nav-section">
            <div 
              className="mobile-nav-header d-flex justify-content-between align-items-center"
              onClick={() => toggleMobileCollapse('reports')}
            >
              <span>Reports</span>
              <i className={`feather ${isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'}`}></i>
            </div>
            <Collapse in={isExpanded}>
              <div>
                {['SA', 'A', 'HR'].includes(permissionLevel) && (
                  <Link to="/report/ExternalCandidateReport" className={baseClass} onClick={handleLinkClick}>
                    External Candidate Report
                  </Link>
                )}
                <Link to="/report/SelfAssessmentReport" className={baseClass} onClick={handleLinkClick}>
                  Self Assessment Report
                </Link>
                {showAssessmentCycle && (
                  <Link to="/report/AssessmentCycleReport" className={baseClass} onClick={handleLinkClick}>
                    Assessment Cycle Report
                  </Link>
                )}
                {['SA', 'A'].includes(permissionLevel) && (
                  <Link to="/report/CycleComparisonReport" className={baseClass} onClick={handleLinkClick}>
                    Cycle Comparison Report
                  </Link>
                )}
              </div>
            </Collapse>
          </div>
        );

      case 'analytics':
        return (
          <div key="analytics" className="mobile-nav-section">
            <div 
              className="mobile-nav-header d-flex justify-content-between align-items-center"
              onClick={() => toggleMobileCollapse('analytics')}
            >
              <span>Analytics</span>
              <i className={`feather ${isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'}`}></i>
            </div>
            <Collapse in={isExpanded}>
              <div>
                {reportList?.reportList?.map((item, index) => (
                  <Link
                    key={index}
                    to="/analytics/AssessmentCycleReport"
                    state={{ url: reportList?.analyticsReport?.['url'], title: item.name }}
                    className={baseClass}
                    onClick={handleLinkClick}
                    onMouseEnter={() => setId(item.id)}
                  >
                    <i className="feather icon-arrow-up-right" />
                    {item.name}
                  </Link>
                ))}
              </div>
            </Collapse>
          </div>
        );

      default:
        return null;
    }
  };

  // Desktop navigation rendering (keep existing code)
  const renderDesktopNavItem = (itemId) => {
    switch (itemId) {
      case 'assessmentCycle':
        return (
          <ListGroup.Item as="li" bsPrefix=" " className="nav-item" key="assessmentCycle">
            <Dropdown align="end" ref={dropdownRefs?.assestCycle}>
              <Dropdown.Toggle
                as={Link}
                onMouseEnter={() => { clearCloseTimeout(); toggleDropdown('assestCycle'); setHoveredDropdown('assestCycle'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
                onClick={() => closeDropdown()}
                variant="link"
                to="#"
                id="dropdown-basic1"
              >
               Assessment Cycle
              </Dropdown.Toggle>
              <Dropdown.Menu
                show={dropdownVisibility.assestCycle}
                align="end"
                className="profile-notification"
                onMouseEnter={() => { clearCloseTimeout(); setHoveredDropdown('assestCycle'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
              >
                <ListGroup as="ul" bsPrefix="1" variant="flush" className="pro-body">
                  <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                    <Link to="/AssessmentCycle/AssessmentCycle" className="dropdown-item">
                      Assessment Cycle
                    </Link>
                  </ListGroup.Item>
                  <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                    <Link to="/AssessmentCycle/QuestionBank" className="dropdown-item">
                      Question Bank
                    </Link>
                  </ListGroup.Item>
                  <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                    <Link to="/AssessmentCycle/AssessorEmployeeMapping" className="dropdown-item">
                      Assessor Employee Mapping
                    </Link>
                  </ListGroup.Item>
                </ListGroup>
              </Dropdown.Menu>
            </Dropdown>
          </ListGroup.Item>
        );

      case 'technicalAssessment':
        return (
          <ListGroup.Item as="li" bsPrefix=" " className="nav-item" key="technicalAssessment">
            <Dropdown align="end" ref={dropdownRefs?.techAssest}>
              <Dropdown.Toggle
                as={Link}
                onMouseEnter={() => { clearCloseTimeout(); toggleDropdown('techAssest'); setHoveredDropdown('techAssest'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
                onClick={() => closeDropdown()}
                variant="link"
                to="#"
                id="dropdown-basic2"
              >
                Technical Assessment
              </Dropdown.Toggle>
              <Dropdown.Menu
                show={dropdownVisibility.techAssest}
                align="end"
                className="profile-notification"
                onMouseEnter={() => { clearCloseTimeout(); setHoveredDropdown('techAssest'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
              >
                <ListGroup as="ul" bsPrefix="1" variant="flush" className="pro-body">
                  <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                    <Link to="/TechnicalAssessment/TechnicalAssessments" className="dropdown-item">
                      Technical Assessment
                    </Link>
                  </ListGroup.Item>
                  <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                    <Link to="/TechnicalAssessment/TechnicalQuestionBank" className="dropdown-item">
                      Technical Question Bank
                    </Link>
                  </ListGroup.Item>
                  {['SA', 'A'].includes(permissionLevel) && (
                    <>
                      <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                        <Link to="/TechnicalAssessment/ManageSmeMappings" className="dropdown-item">
                          Manage SME Mappings
                        </Link>
                      </ListGroup.Item>
                      <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                        <Link to="/TechnicalAssessment/ApproveReviewModifications" className="dropdown-item">
                          Approve Review Modifications
                        </Link>
                      </ListGroup.Item>
                    </>
                  )}
                </ListGroup>
              </Dropdown.Menu>
            </Dropdown>
          </ListGroup.Item>
        );

      case 'assessor':
        return (
          <ListGroup.Item as="li" bsPrefix=" " className="nav-item" key="assessor">
            <Dropdown align="end" ref={dropdownRefs?.assessor}>
              <Dropdown.Toggle
                as={Link}
                onMouseEnter={() => { clearCloseTimeout(); toggleDropdown('assessor'); setHoveredDropdown('assessor'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
                onClick={() => closeDropdown()}
                variant="link"
                to="#"
                id="dropdown-basic3"
              >
                Assessor
              </Dropdown.Toggle>
              <Dropdown.Menu
                show={dropdownVisibility.assessor}
                align="end"
                className="profile-notification"
                onMouseEnter={() => { clearCloseTimeout(); setHoveredDropdown('assessor'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
              >
                <ListGroup as="ul" bsPrefix="1" variant="flush" className="pro-body">
                  <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                    <Link to="/Assessor/UserAnalysis/UsersList" className="dropdown-item">
                     Evaluate Employee
                    </Link>
                  </ListGroup.Item>
                </ListGroup>
              </Dropdown.Menu>
            </Dropdown>
          </ListGroup.Item>
        );

      case 'employeeTasks':
        return (
          <ListGroup.Item as="li" bsPrefix=" " className="nav-item" key="employeeTasks">
            <Dropdown align="end" ref={dropdownRefs?.employeeTask}>
              <Dropdown.Toggle
                as={Link}
                onMouseEnter={() => { clearCloseTimeout(); toggleDropdown('employeeTask'); setHoveredDropdown('employeeTask'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
                onClick={() => closeDropdown()}
                variant="link"
                to="#"
                id="dropdown-basic4"
              >
                Employees Tasks
              </Dropdown.Toggle>
              <Dropdown.Menu
                show={dropdownVisibility.employeeTask}
                align="end"
                className="profile-notification"
                onMouseEnter={() => { clearCloseTimeout(); setHoveredDropdown('employeeTask'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
              >
                <ListGroup as="ul" bsPrefix="1" variant="flush" className="pro-body">
                  <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                    <Link to="/EmployeesTask/AssessmentCycle" className="dropdown-item">
                      My Evaluation Form
                    </Link>
                  </ListGroup.Item>
                  <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                    <Link to="/EmployeesTask/SelfAssessments" className="dropdown-item">
                      Self Assessments
                    </Link>
                  </ListGroup.Item>
                  <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                    <Link to="/EmployeesTask/QuestionBankReview" className="dropdown-item">
                      Question Bank Review
                    </Link>
                  </ListGroup.Item>
                </ListGroup>
              </Dropdown.Menu>
            </Dropdown>
          </ListGroup.Item>
        );

      case 'admin':
        return (
          <ListGroup.Item as="li" bsPrefix=" " className="nav-item" key="admin">
            <Dropdown align="end" ref={dropdownRefs?.Admin}>
              <Dropdown.Toggle
                as={Link}
                onMouseEnter={() => { clearCloseTimeout(); toggleDropdown('Admin'); setHoveredDropdown('Admin'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
                onClick={() => closeDropdown()}
                variant="link"
                to="#"
                id="dropdown-basic5"
              >
                Admin
              </Dropdown.Toggle>
              <Dropdown.Menu
                show={dropdownVisibility.Admin}
                align="end"
                className="profile-notification"
                onMouseEnter={() => { clearCloseTimeout(); setHoveredDropdown('Admin'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
              >
                <ListGroup as="ul" bsPrefix="1" variant="flush" className="pro-body">
                  {['SA', 'A'].includes(permissionLevel) && (
                    <>
                      <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                        <Link to="/Admin/ManageEmployee" className="dropdown-item">
                          Manage Employee
                        </Link>
                      </ListGroup.Item>
                      <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                        <Link to="/Admin/ManageAssessor" className="dropdown-item">
                          Manage Assessor
                        </Link>
                      </ListGroup.Item>
                      <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                        <Link to="/Admin/ManageSkill" className="dropdown-item">
                          Manage Skill
                        </Link>
                      </ListGroup.Item>
                    </>
                  )}
                  <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                    <Link to="/Admin/ManageExternalUser" className="dropdown-item">
                      Manage External User
                    </Link>
                  </ListGroup.Item>
                  {['SA', 'A'].includes(permissionLevel) && (
                    <>
                      <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                        <Link to="/Admin/ManageEmail" className="dropdown-item">
                          Manage Email
                        </Link>
                      </ListGroup.Item>
                      <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                        <Link to="/Admin/ManageEmployeeCycle" className="dropdown-item">
                          Manage Employee Cycle
                        </Link>
                      </ListGroup.Item>
                    </>
                  )}
                </ListGroup>
              </Dropdown.Menu>
            </Dropdown>
          </ListGroup.Item>
        );

      case 'reports':
        return (
          <ListGroup.Item as="li" bsPrefix=" " className="nav-item" key="reports">
            <Dropdown align="end" ref={dropdownRefs?.reports}>
              <Dropdown.Toggle
                as={Link}
                onMouseEnter={() => { clearCloseTimeout(); toggleDropdown('reports'); setHoveredDropdown('reports'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
                onClick={() => closeDropdown()}
                variant="link"
                to="#"
                id="dropdown-basic6"
              >
                Reports
              </Dropdown.Toggle>
              <Dropdown.Menu
                show={dropdownVisibility.reports}
                align="end"
                className="profile-notification"
                onMouseEnter={() => { clearCloseTimeout(); setHoveredDropdown('reports'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
              >
                <ListGroup as="ul" bsPrefix="1" variant="flush" className="pro-body">
                  {['SA', 'A', 'HR'].includes(permissionLevel) && (
                    <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                      <Link to="/report/ExternalCandidateReport" className="dropdown-item">
                        External Candidate Report
                      </Link>
                    </ListGroup.Item>
                  )}
                  <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                    <Link to="/report/SelfAssessmentReport" className="dropdown-item">
                      Self Assessment Report
                    </Link>
                  </ListGroup.Item>
                  {showAssessmentCycle && (
                    <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                      <Link to="/report/AssessmentCycleReport" className="dropdown-item">
                        Assessment Cycle Report
                      </Link>
                    </ListGroup.Item>
                  )}
                  {['SA', 'A'].includes(permissionLevel) && (
                    <ListGroup.Item as="li" bsPrefix=" " onClick={handleLinkClick}>
                      <Link to="/report/CycleComparisonReport" className="dropdown-item">
                        Cycle Comparison Report
                      </Link>
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </Dropdown.Menu>
            </Dropdown>
          </ListGroup.Item>
        );

      case 'analytics':
        return (
          <ListGroup.Item as="li" bsPrefix=" " className="nav-item" key="analytics">
            <Dropdown align="end" ref={dropdownRefs?.Analytics}>
              <Dropdown.Toggle
                as={Link}
                onMouseEnter={() => { clearCloseTimeout(); toggleDropdown('Analytics'); setHoveredDropdown('Analytics'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
                onClick={() => closeDropdown()}
                variant="link"
                to="#"
                id="dropdown-basic7"
              >
                Analytics
              </Dropdown.Toggle>
              <Dropdown.Menu
                show={dropdownVisibility.Analytics}
                align="end"
                className="profile-notification"
                onMouseEnter={() => { clearCloseTimeout(); setHoveredDropdown('Analytics'); }}
                onMouseLeave={() => { setHoveredDropdown(null); startCloseTimeout(); }}
              >
                <ListGroup as="ul" bsPrefix="1" variant="flush" className="pro-body">
                  {reportList?.reportList?.map((item, index) => (
                    <ListGroup.Item as="li" bsPrefix=" " key={index} onClick={handleLinkClick}>
                      <Link
                        to="/analytics/AssessmentCycleReport"
                        state={{ url: reportList?.analyticsReport?.['url'], title: item.name }}
                        className="dropdown-item"
                        onMouseEnter={() => setId(item.id)}
                      >
                        <i className="feather icon-arrow-up-right" />
                        {item.name}
                      </Link>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Dropdown.Menu>
            </Dropdown>
          </ListGroup.Item>
        );

      default:
        return null;
    }
  };

  // Check if we're in mobile view
  const isMobile = windowWidth < 992;

  if (isMobile) {
  return (
    <div className="mobile-nav-container">
      <style jsx>{`
        .mobile-nav-container {
          width: 100%;
          background: white;
          border-top: 1px solid #dee2e6;
        }
        .mobile-nav-section {
          border-bottom: 1px solid #e6e6e6;
          background: #f5f5f5;
        }
        .mobile-nav-header {
          font-weight: 600;
          font-size: 14px;
          color: #6c757d;
          padding: 12px 16px;
          letter-spacing: 0.5px;
          cursor: pointer;
          user-select: none;
          transition: background-color 0.2s;
        }
        .mobile-nav-header:hover {
          background-color: #e9ecef;
        }
        .mobile-nav-header:active {
          background-color: #dee2e6;
        }
        .feather {
          font-size: 16px;
          transition: transform 0.3s ease;
        }
        .nav-link {
          color: #495057 !important;
          text-decoration: none !important;
          border-bottom: 1px solid #f8f9fa;
          transition: all 0.2s;
          background: white;
          margin: 0;
          padding: 12px 24px !important;
        }
        .nav-link:hover {
          background-color: #f8f9fa !important;
          color: #007bff !important;
          padding-left: 28px !important;
        }
        .collapse-content {
          background: white;
          border-top: 1px solid #e9ecef;
        }
      `}</style>
      {allNavItems.map(item => renderMobileNavItem(item.id))}
    </div>
  );
}

  // Desktop view - show normal navigation
  return (
    <ListGroup as="ul" bsPrefix=" " className="navbar-nav">
      {allNavItems.map(item => renderDesktopNavItem(item.id))}
    </ListGroup>
  );
}